import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Share2 } from "lucide-react";
import { fetchArticle, fetchArticles } from "@/lib/api";
import { EditorialImage } from "@/components/Placeholder";
import EmailSignup from "@/components/EmailSignup";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";

export default function Article() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [related, setRelated] = useState([]);
    const [error, setError] = useState(false);

    useSEO({
        title: article ? `${article.title} — Journal | Chrissa Valanti` : "Journal — Chrissa Valanti",
        description: article ? article.excerpt : "Personal reflections and founder updates from Chrissa Valanti.",
        path: `/journal/${slug}`,
        image: article?.featured_image && article.featured_image.startsWith("/")
            ? `https://chrissavalanti.com${article.featured_image}`
            : undefined,
    });

    useEffect(() => {
        setError(false);
        fetchArticle(slug)
            .then((a) => {
                setArticle(a);
                fetchArticles(a.category)
                    .then((all) => setRelated(all.filter((x) => x.slug !== slug).slice(0, 3)))
                    .catch(() => {});
            })
            .catch(() => setError(true));
        window.scrollTo({ top: 0 });
    }, [slug]);

    const share = async () => {
        try {
            if (navigator.share) {
                await navigator.share({ title: article.title, url: window.location.href });
            } else {
                await navigator.clipboard.writeText(window.location.href);
                toast.success("Link copied to clipboard.");
            }
        } catch {
            /* ignored */
        }
    };

    if (error) {
        return (
            <main className="bg-ivory">
                <div className="container-narrow py-40 text-center">
                    <p className="eyebrow text-oxblood">Not found</p>
                    <h1 className="display-2 mt-6">This article could not be found.</h1>
                    <Link to="/journal" className="btn-secondary mt-10 inline-flex">
                        Back to Journal
                    </Link>
                </div>
            </main>
        );
    }

    if (!article) {
        return (
            <main className="bg-ivory">
                <div className="container-narrow py-40 text-center text-stone">Loading…</div>
            </main>
        );
    }

    return (
        <main className="bg-ivory">
            <section className="pt-36 md:pt-44 pb-12 md:pb-16">
                <div className="container-narrow">
                    <Link to="/journal" className="inline-flex items-center gap-2 eyebrow text-stone hover:text-oxblood">
                        <ArrowLeft size={14} strokeWidth={1.5} /> Journal
                    </Link>
                    <p className="eyebrow text-oxblood mt-10">{article.category}</p>
                    <h1 className="display-1 mt-6">{article.title}</h1>
                    <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-stone">
                        <span>{article.draft ? "Draft" : new Date(article.published_at).toLocaleDateString()}</span>
                        <span>·</span>
                        <span>{article.reading_time} min read</span>
                        <button
                            onClick={share}
                            className="ml-auto inline-flex items-center gap-2 eyebrow text-charcoal hover:text-oxblood"
                            data-testid={`article-share-${article.slug}`}
                        >
                            <Share2 size={14} strokeWidth={1.5} /> Share
                        </button>
                    </div>
                </div>
            </section>

            <section>
                <div className="container-narrow">
                    <EditorialImage
                        src={article.featured_image}
                        alt={article.title}
                        aspect="aspect-[16/9]"
                        overlay="dark"
                    />
                </div>
            </section>

            <section>
                <div className="container-narrow py-16 md:py-24 space-y-6 body-lead text-charcoal/80">
                    {article.draft && (
                        <div className="border border-gold/60 bg-ivory-soft px-5 py-4 text-sm tracking-wide text-charcoal/80">
                            <span className="eyebrow text-oxblood">Draft</span> — This piece is a labelled draft while the
                            journal is being shaped. Wording may evolve.
                        </div>
                    )}
                    {article.content.split("\n\n").map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}

                    {article.featured_quote && (
                        <blockquote className="my-12 pl-6 border-l-2 border-gold">
                            <p className="font-serif italic text-3xl md:text-4xl leading-snug text-charcoal">
                                {article.featured_quote}
                            </p>
                        </blockquote>
                    )}
                </div>
            </section>

            {/* Related */}
            {related.length > 0 && (
                <section>
                    <div className="container-editorial py-16 md:py-24 border-t border-charcoal/10">
                        <p className="eyebrow text-oxblood mb-8">Related</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/journal/${r.slug}`}
                                    className="group block border-t border-charcoal/15 pt-6 hover:border-oxblood transition-colors"
                                >
                                    <p className="eyebrow text-stone">{r.category}</p>
                                    <p className="font-serif text-2xl mt-3 group-hover:text-oxblood transition-colors">
                                        {r.title}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Signup */}
            <section className="bg-charcoal text-ivory">
                <div className="container-narrow py-24 md:py-32">
                    <p className="eyebrow text-gold/90">Continue with me</p>
                    <h2 className="display-2 mt-6 text-ivory">Receive the next letter.</h2>
                    <div className="mt-10">
                        <EmailSignup source={`article:${article.slug}`} dark />
                    </div>
                </div>
            </section>
        </main>
    );
}
